import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { useImpersonationGuard } from '@/hooks/useImpersonationGuard';

export interface ProjectNote {
  id: string;
  project_id: string;
  content: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  profiles?: {
    first_name: string | null;
    last_name: string | null;
  };
}

export const useProjectNotes = (projectId: string) => {
  return useQuery({
    queryKey: ['project-notes', projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('project_notes')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!projectId
  });
};

export const useCreateProjectNote = () => {
  const queryClient = useQueryClient();
  const { isReadOnly } = useImpersonationGuard();

  return useMutation({
    mutationFn: async ({ projectId, content }: { projectId: string; content: string }) => {
      if (isReadOnly) throw new Error('Cannot edit project notes in View Only mode');
      const { data, error } = await supabase
        .from('project_notes')
        .insert({
          project_id: projectId,
          content,
          created_by: (await supabase.auth.getUser()).data.user?.id
        })
        .select('*')
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['project-notes', data.project_id] });
      toast.success('Note added successfully');
    },
    onError: (error) => {
      toast.error('Failed to add note');
      console.error('Error creating note:', error);
    }
  });
};

export const useUpdateProjectNote = () => {
  const queryClient = useQueryClient();
  const { isReadOnly } = useImpersonationGuard();

  return useMutation({
    mutationFn: async ({ noteId, content }: { noteId: string; content: string }) => {
      if (isReadOnly) throw new Error('Cannot edit project notes in View Only mode');
      const { data, error } = await supabase
        .from('project_notes')
        .update({ content })
        .eq('id', noteId)
        .select('*')
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['project-notes', data.project_id] });
      toast.success('Note updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update note');
      console.error('Error updating note:', error);
    }
  });
};

export const useDeleteProjectNote = () => {
  const queryClient = useQueryClient();
  const { isReadOnly } = useImpersonationGuard();

  return useMutation({
    mutationFn: async (noteId: string) => {
      if (isReadOnly) throw new Error('Cannot edit project notes in View Only mode');
      // Get project_id before deleting
      const { data: note } = await supabase
        .from('project_notes')
        .select('project_id')
        .eq('id', noteId)
        .single();

      const { error } = await supabase
        .from('project_notes')
        .delete()
        .eq('id', noteId);

      if (error) throw error;
      return note?.project_id;
    },
    onSuccess: (projectId) => {
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: ['project-notes', projectId] });
      }
      toast.success('Note deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete note');
      console.error('Error deleting note:', error);
    }
  });
};